'use client';

import { useMemo } from 'react';
import { useDigitalCardState } from 'src/components/_common/DigitalCard/_hooks/useDigitalCard';
import styled, { css, keyframes } from 'styled-components';

export interface GlowEffectProps {
  /** box-shadow 색상 배열 — 개수만큼 균등 방향 분배 */
  colors: string[];
  /** 퍼짐 정도 배율 @default 1 */
  spread?: number;
  /** 색상 순환 속도 (0~10), 0이면 정지, 클수록 빠름 @default 0 */
  speed?: number;
  /** 보더 라운드 (px) — face의 borderRadius에서 전달 */
  borderRadius?: number;
}

const ANGLE_OFFSET = (-3 / 4) * Math.PI;

const getPositions = (count: number) =>
  Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 + ANGLE_OFFSET;
    return { cos: Math.cos(angle), sin: Math.sin(angle) };
  });

/** 그림자 설정 */
const SHADOW_CONFIG = {
  OUTER: { X_OFFSET: 20, Y_OFFSET: 20, BLUR: 30, SPREAD: -15 },
  INNER: { X_OFFSET: 7, Y_OFFSET: 7, BLUR: 10, SPREAD: -5 },
  HIGHLIGHT: { BLUR: 13, SPREAD: 4, COLOR: 'rgba(255, 255, 255, 0.3)' },
};

const buildShadows = (colors: string[], positions: { cos: number; sin: number }[], spread: number, colorOffset: number) => {
  const n = colors.length;
  const shadows: string[] = [];

  positions.forEach((pos, posIdx) => {
    const color = colors[(posIdx + colorOffset) % n];

    shadows.push(
      `${Math.round(pos.cos * SHADOW_CONFIG.OUTER.X_OFFSET * spread)}px ${Math.round(pos.sin * SHADOW_CONFIG.OUTER.Y_OFFSET * spread)}px ${Math.round(SHADOW_CONFIG.OUTER.BLUR * spread)}px ${Math.round(SHADOW_CONFIG.OUTER.SPREAD * spread)}px ${color}`,
    );
    shadows.push(
      `${Math.round(pos.cos * SHADOW_CONFIG.INNER.X_OFFSET * spread)}px ${Math.round(pos.sin * SHADOW_CONFIG.INNER.Y_OFFSET * spread)}px ${Math.round(SHADOW_CONFIG.INNER.BLUR * spread)}px ${Math.round(SHADOW_CONFIG.INNER.SPREAD * spread)}px ${color}`,
    );
  });

  shadows.push(`0 0 ${Math.round(SHADOW_CONFIG.HIGHLIGHT.BLUR * spread)}px ${Math.round(SHADOW_CONFIG.HIGHLIGHT.SPREAD * spread)}px ${SHADOW_CONFIG.HIGHLIGHT.COLOR}`);
  return shadows.join(', ');
};

/**
 * [글로우 효과 컴포넌트]
 *
 * @description
 * 카드 뒤에 box-shadow 기반 후광 효과를 렌더링합니다.
 * colors 배열 개수에 따라 균등한 각도로 그림자가 분배됩니다.
 * speed > 0이면 색상이 위치를 순환하며 흐르는 애니메이션이 적용됩니다.
 * 이미지 로드 전에는 렌더링되지 않습니다.
 */
const MAX_DURATION = 10;
const MIN_DURATION = 0.5;

const GlowEffect = ({ colors: rawColors, spread = 1, speed = 0, borderRadius = 0 }: GlowEffectProps) => {
  const { isAllResourcesLoaded } = useDigitalCardState();

  const colors = rawColors.length === 1 ? [rawColors[0], rawColors[0]] : rawColors;
  const duration = Math.max(MAX_DURATION - speed, MIN_DURATION);

  const { boxShadow, animation } = useMemo(() => {
    const positions = getPositions(colors.length);
    const staticShadow = buildShadows(colors, positions, spread, 0);

    if (speed <= 0 || colors.length < 2) {
      return { boxShadow: staticShadow, animation: null };
    }

    const n = colors.length;
    const frames: string[] = [];

    for (let step = 0; step <= n; step++) {
      const percent = Math.round((step / n) * 100);
      frames.push(`${percent}% { box-shadow: ${buildShadows(colors, positions, spread, step)}; }`);
    }

    return {
      boxShadow: staticShadow,
      animation: keyframes`${frames.join('\n')}`,
    };
  }, [colors, spread, speed]);

  if (!isAllResourcesLoaded) return null;

  return <GlowBoxShadow $boxShadow={boxShadow} $animation={animation} $duration={duration} $borderRadius={borderRadius} />;
};

export default GlowEffect;

/** 카드 뒤 box-shadow 후광 */
const GlowBoxShadow = styled.div<{
  $boxShadow: string;
  $animation: ReturnType<typeof keyframes> | null;
  $duration: number;
  $borderRadius: number;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: ${({ $borderRadius }) => $borderRadius}px;
  box-shadow: ${({ $boxShadow }) => $boxShadow};
  ${({ $animation, $duration }) =>
    $animation &&
    css`
      animation: ${$animation} ${$duration}s linear infinite;
    `}
  z-index: -1;
`;
